/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Shared (RN + NativeWind) shell composition for the chat home —
// Figma node 3281:98498. Layout:
//
//   <Column>
//     <TopBar />                  full viewport width, bg surface-app
//     <Row flex-1>
//       <Sidebar />               72-wide rail
//       <View card>               rounded-tl-[24] + border + surface-panel
//         {main}
//       </View>
//     </Row>
//   </Column>
//
// The TopBar sits outside the rounded main card so it can span the
// full viewport (Figma's `top-bar` is 1920 wide). The search input
// inside `TopBar` left-pads itself to align with the card's left
// edge.

import type { ReactNode } from "react";
import { StyleSheet, View } from "react-native";

export interface AppShellProps {
	readonly sidebar: ReactNode;
	readonly main: ReactNode;
	/**
	 * Optional top bar rendered above the sidebar+main row. Web hosts
	 * may pass `null` if the surface doesn't need a top chrome.
	 */
	readonly topBar?: ReactNode;
	// Modal slot — settings, status, new-chat, etc. Composed by hosts
	// as siblings; the shared shell stays agnostic.
	readonly modals?: ReactNode;
}

export function AppShell(props: AppShellProps): JSX.Element {
	const { sidebar, main, topBar, modals } = props;
	return (
		<View
			testID="app-shell"
			accessibilityLabel="ping_shell_root"
			// `h-full` (Tailwind `height: 100%`) fills the `#root`, which
			// `index.html` pins to exactly one viewport tall — including the
			// `calc(100vh / zoom)` compensation for the app "Zoom scale". Using
			// `h-screen` (`100vh`) here instead would re-assert the raw
			// viewport height and, under a zoom < 1, leave a bottom gap (the
			// `#root` is already zoom-scaled). `h-full` keeps the sidebar's
			// bottom slot (status / settings / avatar) pinned with no page
			// scroll — the main card's internal `<ScrollView>`s own overflow.
			//
			// On native (RN-macOS via NativeWind/css-interop), the `vh`
			// CSS unit doesn't translate to a runtime pixel value, so
			// `h-screen` resolves to nothing — the root View has no
			// height, every `flex-1` child collapses, and inner
			// ScrollViews end up with 0 visible area (no scroll
			// surface; the user can't scroll the chat list / message
			// pane). RN's AppContainer provides `flex: 1` on the
			// root, so a literal `style={{ flex: 1 }}` here lets the
			// shell fill the actual window. On web, `flex: 1` on a
			// non-flex-parent is a no-op, so `h-screen` continues to
			// be the source of truth and the existing layout is
			// preserved unchanged.
			style={styles.root}
			className="flex flex-col h-full w-full overflow-hidden bg-light-surface-app dark:bg-dark-surface-app"
		>
			{topBar}
			<View style={styles.flex1} className="flex-1 min-h-0 flex-row">
				{sidebar}
				{/* Main card: rounded top-left, 1-px subtle border, surface-panel bg. */}
				<View
					style={styles.flex1}
					className="flex-1 min-w-0 min-h-0 overflow-hidden rounded-tl-[24px] border border-light-border-subtle dark:border-dark-border-subtle bg-light-surface-panel dark:bg-dark-surface-panel"
				>
					{main}
				</View>
			</View>
			{modals}
		</View>
	);
}

const styles = StyleSheet.create({
	// `flex: 1` is a no-op on web for elements outside a flex
	// container, but mandatory on RN-macOS where the NativeWind
	// classes above can't carry viewport / percentage heights.
	root: { flex: 1 },
	flex1: { flex: 1 },
});
